import { useEffect, useRef, useState } from 'react'

import { InlineAdSlotWrapper, StyledInlineAdSlot } from '@/components-page/srp/advertisement/styled'
import {
  GPT_COMPONENT_ID,
  INLINE_AD_POSITIONS,
  INLINE_GRID_AD_POSITIONS,
} from '@/features/advertisement/constants/adSlots'
import { useAdIframe } from '@/features/advertisement/hooks/useAdIframe'
import { useMediaQuery } from '@/hooks/client-only/useMediaQuery'

export type InlineAdSlotProps = {
  index: number
  totalListings: number
  isGridView: boolean
}

const GRID_VIEW_ADS_BREAKPOINT = 832

export const InlineAdSlot = ({ index, totalListings, isGridView }: InlineAdSlotProps) => {
  const { isMinBreakpoint } = useMediaQuery(GRID_VIEW_ADS_BREAKPOINT)

  // inline ad slots occupy ad positions 3-41, see Miro
  // https://miro.com/app/board/uXjVOhxPqW0=/?share_link_id=383582596557
  const adPositionNumber = index

  const adPositions =
    isGridView && !isMinBreakpoint ? INLINE_GRID_AD_POSITIONS : INLINE_AD_POSITIONS

  // need the index for creating the div id
  const indexOfPositionSetting = adPositions.findIndex((position) => position === adPositionNumber)

  const adRef = useRef<HTMLLIElement>(null)
  const { adWidth } = useAdIframe(adRef)
  const [isAdPresent, setIsAdPresent] = useState<boolean>(adWidth > 0)

  useEffect(() => {
    setIsAdPresent(adWidth > 0)
  }, [adWidth])

  // findIndex returns -1 if no match is found
  if (indexOfPositionSetting === -1) return null

  // GOT ids for inline ads are numbered 1 to 4
  const inlineAdId = `${GPT_COMPONENT_ID.INLINE}_${indexOfPositionSetting + 1}`

  // inline ads should never be after the final listing
  const isNotLastListing = index < totalListings - 1

  // AdSlots must be a div; wrapped in an li for a11y.
  return isNotLastListing ? (
    <InlineAdSlotWrapper ref={adRef} isAdPresent={isAdPresent} isGridView={isGridView}>
      <StyledInlineAdSlot key={inlineAdId} id={inlineAdId} data-testid={inlineAdId} />
    </InlineAdSlotWrapper>
  ) : null
}
